// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { getThumbnailUrl } from '@utils/thumbnailUrl';

import { css, cx, keyframes } from '@emotion/css';
import { Theme, useTheme, Typography, Avatar } from '@mui/material';

import type { DriverSelectDriver } from './DriverSelect';

// HELPERS ------------------------------------------------------------------- //

function getDriverInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// TYPES --------------------------------------------------------------------- //

interface DriverSelectItemProps {
  driver: DriverSelectDriver;
  isSelected: boolean;
  onClick: () => void;
  /** Optional extra content rendered to the right of the driver info */
  renderExtra?: (driver: DriverSelectDriver) => React.ReactNode;
  /** Override the selected-state class for context-specific styling */
  selectedClassName?: string;
}

// COMPONENT ----------------------------------------------------------------- //

const DriverSelectItem = ({ driver, isSelected, onClick, renderExtra, selectedClassName }: DriverSelectItemProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const isOnline = driver.status === 'online';
  const displayName = driver.user?.display_name || 'Unknown Driver';
  const initials = getDriverInitials(displayName);

  return (
    <div
      className={cx(cls.item, isSelected ? (selectedClassName || cls.itemSelected) : null)}
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={cls.row}>
        <div className={cls.avatarContainer}>
          <Avatar
            src={getThumbnailUrl(driver.user?.avatar_url, 32) || undefined}
            sx={{
              width: 32,
              height: 32,
              fontSize: 12,
              fontWeight: 600,
              backgroundColor: isOnline ? theme.palette.primary.main : theme.palette.grey[400],
            }}
          >
            {initials}
          </Avatar>
          <div className={cx(cls.statusDot, isOnline ? cls.statusOnline : cls.statusOffline)} />
        </div>

        <div className={cls.info}>
          <Typography
            variant='body2'
            sx={{
              fontWeight: 600,
              fontSize: 13,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              fontSize: 11,
              color: isOnline ? theme.palette.success.main : theme.palette.text.disabled,
              lineHeight: 1.2,
            }}
          >
            {isOnline ? 'Online' : 'Offline'}
          </Typography>
        </div>

        {renderExtra ? renderExtra(driver) : null}
      </div>
    </div>
  );
};

// STYLES -------------------------------------------------------------------- //

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(48, 180, 72, 0.6); }
  70% { box-shadow: 0 0 0 4px rgba(48, 180, 72, 0); }
  100% { box-shadow: 0 0 0 0 rgba(48, 180, 72, 0); }
`;

const useStyles = (theme?: Theme) => ({
  item: css`
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    margin-bottom: 2px;
    min-height: 44px;
    display: flex;
    align-items: center;
    transition: background-color 0.1s ease-in-out;
    &:hover {
      background-color: ${theme?.palette?.action?.hover};
    }
    &:focus-visible {
      outline: 2px solid ${theme?.palette?.primary?.main};
      outline-offset: -2px;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
  itemSelected: css`
    background-color: ${theme?.palette?.primary?.main}12 !important;
    border: 1px solid ${theme?.palette?.primary?.main}30;
  `,
  row: css`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  `,
  avatarContainer: css`
    position: relative;
    flex-shrink: 0;
  `,
  statusDot: css`
    position: absolute;
    bottom: 0;
    right: 0;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    border: 2px solid ${theme?.palette?.background?.paper};
  `,
  statusOnline: css`
    background-color: ${theme?.palette?.success?.main};
    animation: ${pulse} 2s infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  statusOffline: css`
    background-color: ${theme?.palette?.grey?.[400]};
  `,
  info: css`
    min-width: 0;
    flex: 1;
  `,
});

// EXPORT -------------------------------------------------------------------- //

export default DriverSelectItem;
